import type { SsoTrialContext } from '@/context/types';

// Holds the trial attribution ids across the Discord SSO redirect. Google does not need this
// (its popup returns to a still-mounted page), but Discord leaves the origin entirely and the
// login call happens on /auth/discord/callback, where no React prop can reach.
//
// Same-origin on purpose: the OAuth `state` would also survive the round trip, but it is
// declared attacker-controlled round-trip data (see oauth-state.ts) and these ids become a
// database row. The flow already requires sessionStorage for the CSRF token, so this adds no
// failure mode that was not already there.
export const SSO_TRIAL_CONTEXT_KEY = 'sso_trial_context';

// Passing null REMOVES the key, and every authorize must call this — see discord-sso-button.tsx.
export function stashSsoTrialContext(context: SsoTrialContext | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!context) {
      window.sessionStorage.removeItem(SSO_TRIAL_CONTEXT_KEY);
      return;
    }
    window.sessionStorage.setItem(SSO_TRIAL_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // Safari private mode / hardened settings throw on storage access. The visitor simply
    // completes the signup without the trial attached, which is today's behaviour.
  }
}

// Reads and removes in one go: the context belongs to exactly one authorize round trip.
export function takeSsoTrialContext(): SsoTrialContext | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SSO_TRIAL_CONTEXT_KEY);
    window.sessionStorage.removeItem(SSO_TRIAL_CONTEXT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as SsoTrialContext;
  } catch {
    return null;
  }
}
